import { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Download, Filter, Palmtree, AlertTriangle, TrendingDown, Clock, Search } from "lucide-react";
import { formatDateES } from "@/lib/dateUtils";

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR_BANDS = {
  green:    { cls: "bg-green-100 text-green-700 border-green-300",   label: "Verde", min: ">10" },
  amber:    { cls: "bg-amber-100 text-amber-700 border-amber-300",   label: "Ámbar", min: "4–10" },
  red:      { cls: "bg-red-100  text-red-700  border-red-300",       label: "Rojo",  min: "1–3" },
  grey:     { cls: "bg-slate-100 text-slate-600 border-slate-300",   label: "Gris",  min: "0" },
  negative: { cls: "bg-red-200  text-red-800  border-red-400 font-bold", label: "Negativo", min: "<0" },
};

const STATUS_TINY = {
  pendiente_jefe: "Pte. jefe",
  pendiente_gerente: "Pte. gerente",
  en_aprobacion: "Pte.",
  aprobada: "Aprobada",
  rechazada: "Rechazada",
  cancelada_empleado: "Cancelada",
  cancelada: "Cancelada",
  comunicada: "Comunicada",
  documentacion_pendiente: "Doc. pte.",
};

// Iter 234 (P1) — Vista rápida de saldos de vacaciones por empleado.
// Permite a RRHH detectar en <5 segundos quién está cerca de agotar saldo,
// quién tiene pendientes y quién está en saldo negativo. Soporta filtros,
// export Excel y un resumen agregado en la cabecera.
export default function BalancesTab({ axiosAuth, departments = [], centers = [] }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [center, setCenter] = useState("");
  const [department, setDepartment] = useState("");
  const [search, setSearch] = useState("");
  const [scope, setScope] = useState("all"); // all | low | negative | pending
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { year };
      if (center) params.center = center;
      if (department) params.department = department;
      if (scope === "low") params.low_balance = true;
      if (scope === "negative") params.negative_only = true;
      if (scope === "pending") params.with_pending_only = true;
      const res = await axiosAuth().get("/hr/vacation-balances", { params });
      setItems(res.data.items || []);
    } catch {
      toast.error("Error al cargar saldos de vacaciones");
    } finally {
      setLoading(false);
    }
  }, [axiosAuth, year, center, department, scope]);

  useEffect(() => { load(); }, [load]);

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const s = search.trim().toLowerCase();
    return items.filter(i =>
      (i.employee_name || "").toLowerCase().includes(s)
      || (i.center || "").toLowerCase().includes(s)
      || (i.department || "").toLowerCase().includes(s)
    );
  }, [items, search]);

  // KPI agregados (sobre los datos cargados — respeta filtros del servidor)
  const kpis = useMemo(() => {
    const stats = { total: items.length, green: 0, amber: 0, red: 0, grey: 0, negative: 0, with_pending: 0 };
    items.forEach(i => {
      stats[i.color_band] = (stats[i.color_band] || 0) + 1;
      if (i.pending_days > 0) stats.with_pending += 1;
    });
    return stats;
  }, [items]);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({ year });
      if (center) params.set("center", center);
      if (department) params.set("department", department);
      if (scope === "low") params.set("low_balance", "true");
      if (scope === "negative") params.set("negative_only", "true");
      if (scope === "pending") params.set("with_pending_only", "true");
      const url = `${API}/api/hr/vacation-balances/export.xlsx?${params.toString()}`;
      const res = await axiosAuth().get(url.replace(API + "/api", ""), { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `saldos_vacaciones_${year}.xlsx`;
      link.click();
      toast.success("Excel descargado");
    } catch {
      toast.error("Error al exportar");
    }
  };

  const years = [];
  for (let y = currentYear - 2; y <= currentYear + 1; y++) years.push(y);

  return (
    <div className="space-y-4" data-testid="balances-tab">
      {/* KPI agregados */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
        <Card className="border-slate-200 rounded-sm">
          <CardContent className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Total empleados</div>
            <div className="text-2xl font-bold text-slate-900">{kpis.total}</div>
          </CardContent>
        </Card>
        <Card className="border-green-200 rounded-sm bg-green-50/40">
          <CardContent className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-green-700">Saldo holgado</div>
            <div className="text-2xl font-bold text-green-700" data-testid="kpi-green">{kpis.green}</div>
            <div className="text-[9px] text-slate-500 mt-0.5">+10 días disponibles</div>
          </CardContent>
        </Card>
        <Card className="border-amber-200 rounded-sm bg-amber-50/40">
          <CardContent className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-amber-700">En vigilancia</div>
            <div className="text-2xl font-bold text-amber-700" data-testid="kpi-amber">{kpis.amber}</div>
            <div className="text-[9px] text-slate-500 mt-0.5">4–10 días</div>
          </CardContent>
        </Card>
        <Card className="border-red-200 rounded-sm bg-red-50/40">
          <CardContent className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-red-700">Cerca de agotar</div>
            <div className="text-2xl font-bold text-red-700" data-testid="kpi-red">{kpis.red}</div>
            <div className="text-[9px] text-slate-500 mt-0.5">1–3 días restantes</div>
          </CardContent>
        </Card>
        <Card className="border-red-300 rounded-sm bg-red-100/60">
          <CardContent className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-red-800 flex items-center gap-1">
              <TrendingDown className="w-3 h-3" /> Saldo negativo
            </div>
            <div className="text-2xl font-bold text-red-800" data-testid="kpi-negative">{kpis.negative}</div>
            <div className="text-[9px] text-slate-500 mt-0.5">Excedido</div>
          </CardContent>
        </Card>
        <Card className="border-blue-200 rounded-sm bg-blue-50/40">
          <CardContent className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-blue-700 flex items-center gap-1">
              <Clock className="w-3 h-3" /> Con pendientes
            </div>
            <div className="text-2xl font-bold text-blue-700" data-testid="kpi-with-pending">{kpis.with_pending}</div>
            <div className="text-[9px] text-slate-500 mt-0.5">Esperan aprobación</div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card className="border-slate-200 rounded-sm">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Filter className="w-3.5 h-3.5 text-slate-400" />
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-28 rounded-sm h-8 border-slate-200 text-xs" data-testid="balances-year"><SelectValue /></SelectTrigger>
              <SelectContent>
                {years.map(y => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}
              </SelectContent>
            </Select>

            <Select value={center || "all"} onValueChange={(v) => setCenter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-44 rounded-sm h-8 border-slate-200 text-xs" data-testid="balances-center">
                <SelectValue placeholder="Todos los centros" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los centros</SelectItem>
                {centers.map(c => (<SelectItem key={c.id || c.name} value={c.name}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>

            <Select value={department || "all"} onValueChange={(v) => setDepartment(v === "all" ? "" : v)}>
              <SelectTrigger className="w-44 rounded-sm h-8 border-slate-200 text-xs" data-testid="balances-department">
                <SelectValue placeholder="Todos los dptos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los departamentos</SelectItem>
                {departments.map(d => (<SelectItem key={d.id || d.name} value={d.name}>{d.name}</SelectItem>))}
              </SelectContent>
            </Select>

            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="w-52 rounded-sm h-8 border-slate-200 text-xs" data-testid="balances-scope">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los empleados</SelectItem>
                <SelectItem value="low" data-testid="scope-low">Solo con saldo bajo (1–3)</SelectItem>
                <SelectItem value="negative" data-testid="scope-negative">Solo con saldo negativo</SelectItem>
                <SelectItem value="pending" data-testid="scope-pending">Solo con solicitudes pendientes</SelectItem>
              </SelectContent>
            </Select>

            <div className="relative flex-1 min-w-[160px] max-w-xs">
              <Search className="absolute left-2 top-2 w-3.5 h-3.5 text-slate-400" />
              <Input
                placeholder="Buscar empleado, centro, dpto..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="rounded-sm h-8 pl-7 border-slate-200 text-xs"
                data-testid="balances-search"
              />
            </div>

            <Button
              type="button"
              onClick={handleExport}
              variant="outline"
              size="sm"
              className="rounded-sm text-xs uppercase h-8 ml-auto"
              data-testid="balances-export-btn"
            >
              <Download className="w-3 h-3 mr-1" /> Excel
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card className="border-slate-200 rounded-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Empleado</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Centro</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Departamento</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-right">Asignados</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-right">Aprobados</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-right">Pendientes</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-right">Disponibles</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Última solicitud</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Cargando saldos...</TableCell></TableRow>
                ) : filteredItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-10 text-slate-400" data-testid="balances-empty">
                      <Palmtree className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                      No hay empleados que cumplan los filtros aplicados
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredItems.map(it => {
                    const band = COLOR_BANDS[it.color_band] || COLOR_BANDS.grey;
                    return (
                      <TableRow key={it.employee_id} className="hover:bg-slate-50/60" data-testid={`balance-row-${it.employee_id}`}>
                        <TableCell className="text-sm font-medium text-slate-900">{it.employee_name}</TableCell>
                        <TableCell className="text-xs text-slate-600">{it.center || <span className="text-slate-400 italic">—</span>}</TableCell>
                        <TableCell className="text-xs text-slate-600">{it.department || <span className="text-slate-400 italic">—</span>}</TableCell>
                        <TableCell className="text-sm text-right font-mono text-slate-700">{it.annual_allowance}</TableCell>
                        <TableCell className="text-sm text-right font-mono text-slate-700">{it.approved_days}</TableCell>
                        <TableCell className="text-sm text-right font-mono text-slate-700">
                          {it.pending_days > 0 ? (
                            <span className="inline-flex items-center gap-1 text-blue-700">
                              <Clock className="w-3 h-3" /> {it.pending_days}
                            </span>
                          ) : "0"}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge
                            className={`rounded-sm text-xs font-bold border ${band.cls}`}
                            title={`${band.label} (${band.min} días)`}
                            data-testid={`balance-available-${it.employee_id}`}
                          >
                            {it.negative_balance && <AlertTriangle className="w-3 h-3 mr-1" />}
                            {it.available_days}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-slate-600">
                          {it.last_request_date ? (
                            <div className="flex flex-col">
                              <span className="font-mono">{formatDateES(it.last_request_date)}</span>
                              <span className="text-[9px] text-slate-400">
                                {it.last_request_type ? it.last_request_type.replace(/_/g, " ") : ""}
                                {it.last_request_status ? ` · ${STATUS_TINY[it.last_request_status] || it.last_request_status}` : ""}
                              </span>
                            </div>
                          ) : <span className="text-slate-400 italic">Sin solicitudes</span>}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <p className="text-[10px] text-slate-500 italic">
        Las solicitudes <strong>aprobadas</strong> descuentan saldo definitivo.
        Las <strong>pendientes</strong> lo reservan provisionalmente. Bajas médicas, canceladas y rechazadas no descuentan.
      </p>
    </div>
  );
}
